import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PricesDataService, PricesColumnsService } from '../../services';
import { DialogPriceFormComponent } from './price-form/price-form.component';
import { DialogPriceDetailsComponent } from './price-details/price-details.component';
import { Price, PricesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-prices',
  templateUrl: './prices.component.html',
  styleUrls: ['./prices.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PricesComponent implements OnInit, OnDestroy {

  private priceAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private priceEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private priceDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public priceToDelete: Price = {};
  public prices: Price[] = [];
  public price: Price = {};
  public selectedPrices: Price[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private pricesDataService: PricesDataService,
    private pricesColumnsService: PricesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PricesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Prices - Management');
    this.primengConfig.ripple = true;
    this.getPrices();
  }

  ngOnDestroy(): void {
    console.log('PricesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPrices(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.pricesDataService.getPrices(this.parameters).subscribe((response: PricesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.prices = response.results;
        } else {
          this.prices = this.prices.concat(response.results);
        }
        if (response.inlineCount > this.prices.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPrices();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PRICESNOTLOADED')});
    });
  }

  importPrices(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.pricesDataService.addPrices(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PRICESCREATED')});
                if (proceed === j) {
                  this.getPrices();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PRICESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PRICESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.pricesDataService.updatePrices(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PRICESUPDATED')});
                if (proceed === j) {
                  this.getPrices();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PRICESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PRICESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.pricesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('prices.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPrices() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Prices?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.pricesDataService.deletePrices(this.selectedPrices).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Prices Deleted', life: 3000});
            this.getPrices();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Prices not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Prices not deleted'});
        });
        this.selectedPrices = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.prices, this.columns, 'Prices');
  }

  exportExcel() {
    ToolsService.exportExcel(this.prices, this.columns, 'Prices');
  }

  exportCsv() {
    ToolsService.exportCsv(this.prices, this.columns, 'Prices');
  }

  refresh() {
    this.pricesDataService.clearCache();
    this.getPrices();
  }

  priceAdd(): void {
    if (!this.navigation.getParamValue('prices.addInDialog', true)) {
      this.router.navigate(['/prices/add']);
      return;
    }
    this.priceAddDialog = this.dialogService.open(DialogPriceFormComponent, {
      header: '', // this.translate.instant('NEWPRICE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        price: {id: null},
        users: this.pricesColumnsService.users,
      },
    });

    this.priceAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Price added'});
        this.getPrices();
      }
    });
  }

  priceEdit(price: Price): void {
    if (!this.navigation.getParamValue('prices.editInDialog', true)) {
      this.router.navigate(['/prices/edit/' + price.id]);
      return;
    }
    this.priceEditDialog = this.dialogService.open(DialogPriceFormComponent, {
      header: '', // this.translate.instant('EDITPRICE') + ' ' + price.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        price: price,
        users: this.pricesColumnsService.users,
      },
    });

    this.priceEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Price updated'});
        this.getPrices();
      }
    });
  }

  priceDetails(price: Price): void {
    if (!this.navigation.getParamValue('prices.detailsInDialog', true)) {
      this.router.navigate(['/prices/details/' + price.id]);
      return;
    }
    this.priceDetailsDialog = this.dialogService.open(DialogPriceDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        price: price,
      }
    });
  }

  priceDelete(price: Price): void {
    this.priceToDelete = price;
    this.messageService.clear();
    this.messageService.add({
      key: 'priceDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + price.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.pricesDataService.deletePrice(this.priceToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Price deleted'});
      this.getPrices();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Price not deleted'});
    });
  }

  onDeleteReject(): void {
    this.priceToDelete = {};
    this.messageService.clear('priceDelete');
  }

}
